import React, { PropsWithChildren } from 'react';
import styled from '@emotion/styled';
import {
  Color,
  FontSize,
  FontWeight,
  colors,
  convertChildrenToSentenceCase,
  getFontStyle,
  getSpacing,
  spacing,
} from '@cabonline/ui-shared';

interface CustomStyles {
  marginBottom?: keyof typeof spacing;
  weight?: FontWeight;
  size?: FontSize;
  $color?: Color;
  underline?: boolean;
  truncate?: boolean;
  underlineOffset?: keyof typeof spacing;
  htmlFor?: string;
}

export interface TextProps extends CustomStyles, PropsWithChildren {
  size?: FontSize;
  as?:
    | 'p'
    | 'div'
    | 'span'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'ul'
    | 'li';
  testID?: string;
  color?: Color;
  underline?: boolean;
  truncate?: boolean;
  title?: string;
  underlineOffset?: keyof typeof spacing;
  id?: string;
  sentenceCased?: boolean;
}

const getTextColor = (color?: Color): Color => {
  if (color) {
    return color;
  }
  return colors.black;
};

const StyledText = styled.p<CustomStyles>`
  line-height: normal;
  margin: 0;
  padding: 0;
  ${(p) => getFontStyle(p.size, p.weight)}
  color: ${(p) => getTextColor(p.$color)};
  margin-bottom: ${(p) => p.marginBottom && getSpacing(p.marginBottom)};
  text-decoration: ${(p) => (p.underline ? 'underline' : 'none')};
  overflow: ${(p) => (p.truncate ? 'hidden' : 'visible')};
  text-overflow: ${(p) => (p.truncate ? 'ellipsis' : 'clip')};
  white-space: ${(p) => (p.truncate ? 'nowrap' : 'normal')};
  text-underline-offset: ${(p) =>
    p.underlineOffset ? getSpacing(p.underlineOffset) : 'auto'};
`;

export const Text: React.FC<TextProps> = ({
  htmlFor,
  children,
  size = 'm',
  as = 'p',
  marginBottom,
  weight = 'semi',
  testID = '',
  color = colors.black,
  underline = false,
  truncate = false,
  title,
  underlineOffset,
  sentenceCased = false,
  id,
}) => {
  return (
    <StyledText
      htmlFor={htmlFor}
      id={id}
      as={as}
      marginBottom={marginBottom}
      weight={weight}
      data-testid={testID}
      size={size}
      $color={color}
      underline={underline}
      truncate={truncate}
      title={title}
      underlineOffset={underlineOffset}
    >
      {convertChildrenToSentenceCase(children, sentenceCased)}
    </StyledText>
  );
};
